import { Link } from "@tanstack/react-router"
import { ArrowRightIcon, Share2 } from "lucide-react"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { CERTIFIED_THRESHOLD } from "@/constants/company/certification"

export function CompanyTrustSidebar({
  companyName,
  companyLongSlug,
  trustScore,
  rankLabel,
  isOutOfBusiness,
}: {
  companyName: string
  companyLongSlug: string
  trustScore: number
  rankLabel: string | null
  isOutOfBusiness: boolean
}) {
  const isCertified = trustScore >= CERTIFIED_THRESHOLD
  const scoreValue = Math.round(trustScore)

  const handleShareClick = async () => {
    const shareData = {
      title: companyName,
      url: window.location.href,
    }

    if (navigator.share) {
      try {
        await navigator.share(shareData)
      } catch {
        // User cancelled or share failed silently
      }
    } else {
      await navigator.clipboard.writeText(window.location.href)
      toast.success("Link copied to clipboard")
    }
  }

  return (
    <>
      {/* Mobile sticky header bar */}
      <div className="brand-light fixed inset-x-0 top-0 z-50 flex h-16 items-center justify-between border-b-2 px-4 lg:hidden">
        <div className="flex items-center gap-2">
          <div className="text-5xl font-black">{scoreValue}</div>
          <div className="wb-label font-bold">
            Trust
            <br />
            Score
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="icon" variant="ghost" onClick={handleShareClick}>
            <Share2 />
          </Button>
          {isOutOfBusiness ? (
            <Badge variant="bad">Out of Business</Badge>
          ) : (
            <Button shouldRenderAsChild>
              <Link
                to="/company/$longSlug/contact"
                params={{ longSlug: companyLongSlug }}
              >
                Connect <ArrowRightIcon className="animate-bounce-right" />
              </Link>
            </Button>
          )}
        </div>
      </div>

      {/* Desktop sidebar - yellow panel */}
      <div className="hidden border-2 lg:block">
        <div className="brand-light border-b px-8 py-14">
          <div className="text-center">
            <span className="wb-label font-bold">Trust Score</span>
            <div className="flex items-baseline justify-center gap-0.5 tracking-tighter">
              <span className="text-9xl font-black">{scoreValue}</span>
              <span className="text-2xl opacity-70">/</span>
              <span className="text-2xl opacity-70">100</span>
            </div>
          </div>

          {rankLabel && (
            <div className="mt-2 rotate-2 text-center leading-relaxed">
              <span className="text-background bg-foreground inline decoration-clone px-4 py-1 text-sm font-bold uppercase">
                {rankLabel}
              </span>
            </div>
          )}
        </div>
        <div className="brand-dark space-y-2 p-2">
          <Button
            variant="outline"
            className="w-full"
            onClick={handleShareClick}
          >
            Share Page
          </Button>
          {!isOutOfBusiness && (
            <>
              <Button className="w-full border-none" shouldRenderAsChild>
                <Link
                  to="/company/$longSlug/contact"
                  params={{ longSlug: companyLongSlug }}
                >
                  Get in touch{" "}
                  <ArrowRightIcon className="animate-bounce-right" />
                </Link>
              </Button>
              {isCertified && (
                <div className="flex items-center justify-center gap-2">
                  <img
                    src="/assets/certified.png"
                    alt="WattBot Certified"
                    width={60}
                    height={60}
                    loading="lazy"
                  />
                  <div className="text-left">
                    <div className="text-sm leading-tight font-black uppercase">
                      WattBot <br /> Certified
                    </div>
                    <div className="wb-label-muted">Top 5% installer</div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {isOutOfBusiness && (
        <div className="bg-bad text-bad-foreground mt-3 hidden border-2 py-2 text-center lg:block">
          <span className="wb-label font-bold">Out of Business</span>
        </div>
      )}
    </>
  )
}
